import { PIPELINE_STATUS, type PipelineStatus } from "../constants/pipeline";
import {
  ACTION_OWNER,
  STAGE,
  type ActionOwner,
  type Stage,
  type StageRole,
} from "../constants/stage";
import {
  ORDER_PAYMENT_STATUS,
  type OrderPaymentStatus,
  type OrderStage,
} from "../constants/statuses";

export const STAGE_LABELS: Record<Stage, string> = {
  draft: "Draft",
  submitted: "Submitted",
  vendor_responding: "Vendor responding",
  awaiting_review: "Awaiting review",
  confirming_pickup: "Confirming pickup",
  confirmed_awaiting_payment: "Awaiting payment",
  awaiting_scheduling: "Awaiting scheduling",
  scheduled: "Scheduled",
  in_transit: "In transit",
  delivered: "Delivered",
  complete: "Complete",
  cancelled: "Cancelled",
  refunded: "Refunded",
  expired: "Expired",
};

export function getStageLabel(stage: Stage): string {
  return STAGE_LABELS[stage];
}

const STAGE_PIPELINE_STATUS: Record<Stage, PipelineStatus> = {
  draft: PIPELINE_STATUS.DRAFT,
  submitted: PIPELINE_STATUS.PENDING_CONFIRMATION,
  vendor_responding: PIPELINE_STATUS.PENDING_CONFIRMATION,
  awaiting_review: PIPELINE_STATUS.PENDING_CONFIRMATION,
  confirming_pickup: PIPELINE_STATUS.CONFIRMED,
  confirmed_awaiting_payment: PIPELINE_STATUS.AWAITING_PAYMENT,
  awaiting_scheduling: PIPELINE_STATUS.CONFIRMED,
  scheduled: PIPELINE_STATUS.SCHEDULED,
  in_transit: PIPELINE_STATUS.SHIPPED,
  delivered: PIPELINE_STATUS.DELIVERED,
  complete: PIPELINE_STATUS.COMPLETED,
  cancelled: PIPELINE_STATUS.CANCELLED,
  refunded: PIPELINE_STATUS.CANCELLED,
  expired: PIPELINE_STATUS.CANCELLED,
};

export function projectStageToPipelineStatus(stage: Stage): PipelineStatus {
  return STAGE_PIPELINE_STATUS[stage];
}

export type OrderTrackKey =
  | "draft"
  | "confirmed"
  | "scheduled"
  | "transit"
  | "delivered"
  | "invoiced"
  | "paid";

export function projectStageToOrderTrackKey(
  stage: Stage,
  paymentStatus: OrderPaymentStatus,
): OrderTrackKey {
  if (paymentStatus === ORDER_PAYMENT_STATUS.PAID) {
    return "paid";
  }
  if (
    paymentStatus === ORDER_PAYMENT_STATUS.INVOICED ||
    paymentStatus === ORDER_PAYMENT_STATUS.FAILED
  ) {
    return "invoiced";
  }
  switch (stage) {
    case STAGE.COMPLETE:
    case STAGE.CANCELLED:
    case STAGE.REFUNDED:
    case STAGE.EXPIRED:
      return "paid";
    case STAGE.DELIVERED:
      return "delivered";
    case STAGE.IN_TRANSIT:
      return "transit";
    case STAGE.SCHEDULED:
      return "scheduled";
    case STAGE.CONFIRMING_PICKUP:
    case STAGE.CONFIRMED_AWAITING_PAYMENT:
    case STAGE.AWAITING_SCHEDULING:
      return "confirmed";
    case STAGE.DRAFT:
    case STAGE.SUBMITTED:
    case STAGE.VENDOR_RESPONDING:
    case STAGE.AWAITING_REVIEW:
      return "draft";
  }
}

export interface OrderTimelineStep {
  label: string;
  isComplete: boolean;
}

const TIMELINE_INDEX: Partial<Record<Stage, number>> = {
  confirming_pickup: 0,
  confirmed_awaiting_payment: 0,
  awaiting_scheduling: 0,
  scheduled: 1,
  in_transit: 2,
  delivered: 3,
  complete: 4,
};

const FULFILMENT_INDEX: Partial<Record<OrderStage, number>> = {
  awaiting_pickup: 0,
  in_transit: 2,
  delivered: 3,
  completed: 4,
};

export function projectOrderTimeline(input: {
  resolvedStage: Stage;
  rawStage: OrderStage;
  isPickup: boolean;
}): Array<OrderTimelineStep> {
  const current =
    TIMELINE_INDEX[input.resolvedStage] ??
    FULFILMENT_INDEX[input.rawStage] ??
    -1;
  return [
    { label: "Order confirmed", isComplete: current >= 0 },
    { label: "Scheduled", isComplete: current >= 1 },
    {
      label: input.isPickup ? "Picked up" : "In transit",
      isComplete: current >= 2,
    },
    { label: "Delivered", isComplete: current >= 3 },
    { label: "Completed", isComplete: current >= 4 },
  ];
}

export interface StageNextAction {
  label: string;
  urgent: boolean;
}

export function getOrderStageLabel(stage: Stage, isPickup: boolean): string {
  if (stage === STAGE.IN_TRANSIT && isPickup) return "Picked up";
  return getStageLabel(stage);
}

export function projectStageToNextAction(input: {
  stage: Stage;
  actionOwner: ActionOwner;
  viewerRole: StageRole;
  paymentStatus: OrderPaymentStatus;
  isPickup?: boolean;
  collectsOnTerms?: boolean;
}): StageNextAction {
  const terminalAction = getTerminalAction(input.stage);
  if (terminalAction) return terminalAction;
  if (input.viewerRole === "vendor") return getVendorAction(input);
  if (input.viewerRole === "buyer") return getBuyerAction(input);
  return getAdminAction(input);
}

function getTerminalAction(stage: Stage): StageNextAction | null {
  if (stage === STAGE.CANCELLED) return { label: "Cancelled", urgent: false };
  if (stage === STAGE.REFUNDED) return { label: "Refunded", urgent: false };
  if (stage === STAGE.EXPIRED) return { label: "Expired", urgent: false };
  if (stage === STAGE.COMPLETE) return { label: "Completed", urgent: false };
  return null;
}

function isPaidOrderInAccountingReview(input: {
  stage: Stage;
  actionOwner: ActionOwner;
  paymentStatus: OrderPaymentStatus;
}): boolean {
  return (
    input.stage === STAGE.AWAITING_REVIEW &&
    input.actionOwner === ACTION_OWNER.ADMIN &&
    (input.paymentStatus === ORDER_PAYMENT_STATUS.PAID ||
      input.paymentStatus === ORDER_PAYMENT_STATUS.REFUNDED)
  );
}

function getVendorAction(input: {
  stage: Stage;
  actionOwner: ActionOwner;
  paymentStatus: OrderPaymentStatus;
  isPickup?: boolean;
}): StageNextAction {
  if (input.stage === STAGE.DELIVERED) {
    if (input.paymentStatus === ORDER_PAYMENT_STATUS.PAID) {
      return { label: "Payout processing", urgent: false };
    }
    return input.actionOwner === ACTION_OWNER.BUYER
      ? { label: "Awaiting payment", urgent: false }
      : { label: "Awaiting sign-off", urgent: false };
  }
  if (input.stage === STAGE.IN_TRANSIT) {
    return input.isPickup
      ? { label: "Mark picked up", urgent: true }
      : { label: "Awaiting delivery", urgent: false };
  }
  if (input.stage === STAGE.SCHEDULED) {
    return input.isPickup
      ? { label: "Mark picked up", urgent: true }
      : { label: "Add shipping details", urgent: true };
  }
  if (isPaidOrderInAccountingReview(input)) {
    return { label: "Awaiting Repackify", urgent: false };
  }
  if (
    input.stage === STAGE.CONFIRMING_PICKUP ||
    input.stage === STAGE.AWAITING_SCHEDULING
  ) {
    return input.actionOwner === ACTION_OWNER.VENDOR
      ? { label: "Arrange pickup", urgent: true }
      : { label: "Awaiting buyer", urgent: false };
  }
  return actionOwnerFallback(input.actionOwner, ACTION_OWNER.VENDOR);
}

function getBuyerAction(input: {
  stage: Stage;
  actionOwner: ActionOwner;
  paymentStatus: OrderPaymentStatus;
  isPickup?: boolean;
  collectsOnTerms?: boolean;
}): StageNextAction {
  if (input.stage === STAGE.CONFIRMED_AWAITING_PAYMENT) {
    return input.actionOwner === ACTION_OWNER.BUYER
      ? { label: "Complete payment", urgent: true }
      : { label: "Awaiting approval", urgent: false };
  }
  if (isPaidOrderInAccountingReview(input)) {
    return { label: "Confirming your order", urgent: false };
  }
  if (input.stage === STAGE.AWAITING_REVIEW && input.actionOwner === ACTION_OWNER.ADMIN) {
    return input.collectsOnTerms
      ? { label: "Confirming your order", urgent: false }
      : { label: "Awaiting approval", urgent: false };
  }
  if (input.stage === STAGE.CONFIRMING_PICKUP) {
    return input.actionOwner === ACTION_OWNER.BUYER
      ? { label: "Confirm pickup", urgent: true }
      : { label: "Awaiting vendor", urgent: false };
  }
  if (input.stage === STAGE.IN_TRANSIT) {
    return input.isPickup
      ? { label: "Mark picked up", urgent: true }
      : { label: "Track shipment", urgent: false };
  }
  if (input.stage === STAGE.DELIVERED) {
    return input.actionOwner === ACTION_OWNER.BUYER
      ? { label: "Complete payment", urgent: true }
      : { label: "Delivered", urgent: false };
  }
  if (input.stage === STAGE.SCHEDULED) {
    return input.isPickup
      ? { label: "Mark picked up", urgent: false }
      : { label: "Shipment scheduled", urgent: false };
  }
  if (input.stage === STAGE.AWAITING_SCHEDULING) {
    return { label: "Awaiting confirmation", urgent: false };
  }
  return actionOwnerFallback(input.actionOwner, ACTION_OWNER.BUYER);
}

function getAdminAction(input: { actionOwner: ActionOwner }): StageNextAction {
  return actionOwnerFallback(input.actionOwner, ACTION_OWNER.ADMIN);
}

function actionOwnerFallback(
  actionOwner: ActionOwner,
  viewer: ActionOwner,
): StageNextAction {
  if (actionOwner === viewer) return { label: "Action required", urgent: true };
  if (actionOwner === ACTION_OWNER.SYSTEM) {
    return { label: "In progress", urgent: false };
  }
  return { label: `Awaiting ${actionOwner}`, urgent: false };
}
